import { Component, Input } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, firstValueFrom, Subject, Subscription } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { CreateCampaignDialogComponent } from 'src/app/dialogs/create-campaign-dialog/create-campaign-dialog.component';
import { Campaign, CampaignScheduleValues, CampaignType } from 'src/app/models/campaign';
import { Organization } from 'src/app/models/organization';
import { Paginator } from 'src/app/models/paginator';
import { User } from 'src/app/models/user';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { CampaignService } from 'src/app/services/campaign.service';
import { MessageService } from 'src/app/services/message.service';
import { OrganizationService } from 'src/app/services/organization.service';

@Component({
  selector: 'app-organization-campaigns',
  templateUrl: './organization-campaigns.component.html',
  styleUrl: './organization-campaigns.component.scss'
})
export class OrganizationCampaignsComponent {
  @Input() organization?: Organization;

  loading: boolean = false;
  noDocs: boolean = false;
  done: boolean = false;

  displayedColumns: string[] = ['name', 'type', 'targets', 'schedule', 'lastExecution', 'actions'];

  campaigns: Paginator<any> = {
    page: 1,
    limit: 10,
    offset: 0,
    sort: 'lastName',
    docs: [],
    totalDocs: 0
  }

  public filters: { query?: string } = {
    query: undefined
  }

  private readonly filtersSubject = new Subject<{ query?: string }>();
  private filtersSubscription?: Subscription;

  user?: User;
  me?: User;


  constructor(
    private _message: MessageService,
    private authService: AuthenticationService,
    private campaignService: CampaignService,
    private dialog: MatDialog,
    private organizationService: OrganizationService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
    this.authService.user.subscribe((user: User | undefined) => {
      this.user = user;
      this.me = user;
    });
  }


  async ngOnInit() {
    this.loading = true;
    this.done = false;
    await this.retrieveCampaigns(true);
    this.loading = false;
    this.done = true;
    this.filtersSubscription = this.filtersSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(async () => {
        this.resetPage();
        await this.retrieveCampaigns(false);
      });
  }


  async retrieveCampaigns(init: boolean) {
    try {
      this._message.emitChange("LOADING", "START");

      this.campaigns = { ...this.campaigns, ...await this.campaignService.retrieveAll(this.campaigns.page, this.campaigns.limit, this.campaigns.offset, this.filters.query) };

      if (init && this.campaigns.totalDocs === 0) {
        this.noDocs = true;
      } else if (init && this.campaigns.totalDocs > 0) {
        this.noDocs = false;
      }

      this._message.emitChange("LOADING", "END");
    } catch (err) {
      this._message.emitChange("LOADING", "END");
      this.toastr.error(`ERRORS.GENERIC`)
    }

  }

  resetPage() {
    this.campaigns.limit = 10;
    this.campaigns.page = 1;
    this.campaigns.offset = 0;
  }

  async handlePage(event: any) {
    this.campaigns.limit = event.pageSize;
    this.campaigns.page = event.pageIndex + 1;
    this.campaigns.offset = event.pageIndex * this.campaigns.limit;

    this.retrieveCampaigns(false);
  }

  onSortChange(event: any) {
    if (['asc', 'desc'].includes(event.direction)) {
      this.campaigns.sort = `${event.direction === 'desc' ? '-' : ''}${event.active}`;
      this.retrieveCampaigns(false);
    }
  }

  async create() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        organization: this.organization,
        mode: 'create'
      }
    }
    const dialog = this.dialog.open(CreateCampaignDialogComponent, config);
    const result: { created: boolean, campaign: Campaign } = await firstValueFrom(dialog.afterClosed());
    if (result && result.created && result.campaign) {
      try {
        this._message.emitChange("LOADING", "START");
        await this.campaignService.create(result.campaign);
        this.toastr.success(this.translate.instant(`CAMPAIGN.CREATE_CAMPAIGN_SUCCESS`, result.campaign))
        this._message.emitChange("LOADING", "END");
        this.resetPage();
        this.retrieveCampaigns(false);
      } catch (err) {
        this.loading = false;
        this.toastr.error('ERRORS.GENERIC');
        this._message.emitChange("LOADING", "END");
      }
    }
  }


  async edit(campaign: Campaign) {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        organization: this.organization,
        mode: 'edit',
        campaign
      }
    }
    const dialog = this.dialog.open(CreateCampaignDialogComponent, config);
    const result: { edited: boolean, campaign: Campaign } = await firstValueFrom(dialog.afterClosed());
    if (result && result.edited && result.campaign) {
      try {
        this._message.emitChange("LOADING", "START");
        await this.campaignService.update(campaign._id, result.campaign);
        this.toastr.success(this.translate.instant(`CAMPAIGN.EDIT_CAMPAIGN_SUCCESS`, result.campaign))
        this._message.emitChange("LOADING", "END");
        this.resetPage();
        this.retrieveCampaigns(false);
      } catch (err) {
        this.loading = false;
        this.toastr.error('ERRORS.GENERIC');
        this._message.emitChange("LOADING", "END");
      }
    }
  }

  async toggle(campaign: Campaign) {
    const toUpdate = JSON.parse(JSON.stringify(campaign));
    toUpdate.enabled = !toUpdate.enabled;
    try {
      campaign = await this.campaignService.update(campaign._id, toUpdate);
      this.resetPage();
      await this.retrieveCampaigns(false);
      this.toastr.success(this.translate.instant(toUpdate.enabled ? `CAMPAIGN.ENABLE_CAMPAIGN_SUCCESS` : `CAMPAIGN.DISABLE_CAMPAIGN_SUCCESS`, campaign))
    } catch (err) {
      this.loading = false;
      this.toastr.error('ERRORS.GENERIC');
    }

  }

  async delete(campaign: Campaign) {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'ORGANIZATION.DELETE_CAMPAIGN',
        text: this.translate.instant('ORGANIZATION.DELETE_CAMPAIGN_HINT', campaign),
        button: {
          text: 'ACTIONS.DELETE',
          class: 'danger-button'
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result && result.confirmed) {
      await this.campaignService.delete(campaign._id);
      this.resetPage();
      await this.retrieveCampaigns(false);
      this.toastr.success(this.translate.instant(`CAMPAIGN.DELETE_CAMPAIGN_SUCCESS`, campaign))
    }
  }

  hasTooltip(campaign: Campaign) {
    return !campaign.enabled || this.isCampaignInactive(campaign)
  }

  isCampaignInactive(campaign: Campaign) {
    return campaign.schedule === CampaignScheduleValues[5] && campaign.lastExecution && moment(campaign.lastExecution.executedAt).isSameOrBefore(moment())
  }

  campaignTooltip(campaign: Campaign) {
    if (!campaign.enabled) {
      return this.translate.instant('CAMPAIGN.CAMPAIGN_DISABLED')
    } else if (this.isCampaignInactive(campaign)) {
      return this.translate.instant('CAMPAIGN.CAMPAIGN_INACTIVE')
    }
    return undefined;
  }

  formatDate(date: any) {
    return moment(date).format('lll')
  }

  nextExecution() {

  }


}
